import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { format, parseISO } from "date-fns";
import {
  CarFront,
  ChevronRight,
  CreditCard,
  DollarSign,
  FileText,
  Home,
  Plug,
  Trash2,
} from "lucide-react";
import { useState } from "react";
import { EmptyState } from "@/components/empty-state";
import { PageHeader } from "@/components/page-header";
import { PageLoading } from "@/components/page-loading";
import { PageShell } from "@/components/page-shell";
import { Section } from "@/components/section";
import { Button } from "@/components/ui/button";
import { FormError } from "@/components/ui/form-error";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useConfirm } from "@/components/use-confirm";
import {
  type Document,
  documentsQueryOptions,
  type DocumentType,
  documentTypesQueryOptions,
  ensureConsumerQueryData,
  formOptionsQueryOptions,
  useDeleteDocumentMutation,
  useUpdateDocumentMutation,
} from "@/lib/data";
import { requireSession } from "@/lib/session";
import { cn, labelFor } from "@/lib/utils";

const CATEGORY_CARDS = [
  { key: "income", icon: DollarSign, label: "Income" },
  { key: "residence", icon: Home, label: "Residence" },
  { key: "utility_bill", icon: Plug, label: "Utility Bill" },
  { key: "auto_insurance", icon: CarFront, label: "Auto Insurance" },
  { key: "identity", icon: CreditCard, label: "Identity" },
] as const;

const FILTER_CHIPS = [
  { key: "all", label: "All" },
  { key: "income", label: "Income" },
  { key: "residence", label: "Residence" },
  { key: "utility_bill", label: "Utility" },
  { key: "auto_insurance", label: "Auto insurance" },
  { key: "identity", label: "Identity" },
  { key: "other", label: "Other" },
];

function groupTypesByCategory(types: DocumentType[]) {
  const groups: Record<string, DocumentType[]> = {};
  for (const type of types) {
    const cat = type.category;
    if (!groups[cat]) groups[cat] = [];
    groups[cat].push(type);
  }
  return groups;
}

/**
 * A document's category comes from its document type. The API's own
 * `document_category` field is a different (dealer-side) enum that is never
 * populated for consumer uploads, so it can't be used for filtering.
 */
function documentCategory(document: Document, types: DocumentType[]) {
  return (
    types.find((t) => t.id === document.uploaded_document_type_id)?.category ??
    "other"
  );
}

function GroupedDocumentTypeOptions({ types }: { types: DocumentType[] }) {
  const { data: formOptions } = useQuery(formOptionsQueryOptions);
  const groups = groupTypesByCategory(types);
  return (
    <>
      {Object.entries(groups).map(([category, items]) => (
        <SelectGroup key={category}>
          <SelectLabel>
            {labelFor(formOptions?.document_categories, category, category)}
          </SelectLabel>
          {items.map((type) => (
            <SelectItem key={type.id} value={type.id}>
              {type.name}
            </SelectItem>
          ))}
        </SelectGroup>
      ))}
    </>
  );
}

export const Route = createFileRoute("/documents")({
  component: DocumentsPage,
  beforeLoad: requireSession,
  loader: () =>
    Promise.all([
      ensureConsumerQueryData(documentsQueryOptions),
      ensureConsumerQueryData(documentTypesQueryOptions),
    ]),
});

function DocumentsPage() {
  const navigate = useNavigate();
  const confirm = useConfirm();
  const { data: docsData, isLoading: docsLoading } = useQuery(
    documentsQueryOptions,
  );
  const { data: docTypes, isLoading: typesLoading } = useQuery(
    documentTypesQueryOptions,
  );
  const deleteDocument = useDeleteDocumentMutation();
  const [categoryFilter, setCategoryFilter] = useState<string>("all");

  const documents = docsData?.documents ?? [];
  const typesByCategory = groupTypesByCategory(docTypes ?? []);
  const isLoading = docsLoading || typesLoading;

  const visibleDocuments =
    categoryFilter === "all"
      ? documents
      : documents.filter(
          (d) => documentCategory(d, docTypes ?? []) === categoryFilter,
        );

  async function handleDelete(id: string) {
    const ok = await confirm({
      title: "Remove this document?",
      description: "This document will be deleted from your application.",
      confirmLabel: "Remove",
      destructive: true,
    });
    if (ok) deleteDocument.mutate(id);
  }

  function handleCategoryClick(categoryKey: string) {
    void navigate({ to: "/documents/new", search: { category: categoryKey } });
  }

  if (isLoading) {
    return <PageLoading />;
  }

  return (
    <PageShell>
      <PageHeader
        title="Documents"
        description="Upload supporting paperwork or reclassify what you've already shared."
      />

      {/* The mutation clears its own error when the next attempt starts. */}
      {deleteDocument.isError && (
        <div className="mb-4">
          <FormError message="Couldn't remove that document. Please try again." />
        </div>
      )}

      {documents.length > 0 ? (
        <div className="mb-6 space-y-4">
          <div className="-mx-4 overflow-x-auto px-4">
            <div className="flex gap-2">
              {FILTER_CHIPS.map((chip) => {
                const isActive = categoryFilter === chip.key;
                const count =
                  chip.key === "all"
                    ? documents.length
                    : documents.filter(
                        (d) => documentCategory(d, docTypes ?? []) === chip.key,
                      ).length;
                if (chip.key !== "all" && count === 0) return null;
                return (
                  <button
                    key={chip.key}
                    type="button"
                    onClick={() => setCategoryFilter(chip.key)}
                    className={cn(
                      "inline-flex shrink-0 items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium transition-colors",
                      isActive
                        ? "border-primary bg-primary text-primary-foreground"
                        : "border-border bg-card text-muted-foreground hover:text-foreground",
                    )}
                  >
                    {chip.label}
                    <span
                      className={cn(
                        "tabular-nums",
                        isActive
                          ? "text-primary-foreground/80"
                          : "text-muted-foreground/70",
                      )}
                    >
                      {count}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
          <div className="space-y-3">
            {visibleDocuments.map((doc) => (
              <DocumentCard
                key={doc.id}
                document={doc}
                documentTypes={docTypes ?? []}
                onDelete={handleDelete}
                deleting={deleteDocument.isPending}
              />
            ))}
          </div>
        </div>
      ) : (
        <div className="mb-6">
          <EmptyState
            icon={<FileText className="size-5" />}
            title="No documents yet"
            description="Pick a category below to upload your first document."
          />
        </div>
      )}

      <Section title="Upload by category">
        <div className="space-y-2">
          {CATEGORY_CARDS.map(({ key, icon: Icon, label }) => (
            <button
              key={key}
              type="button"
              className="group flex w-full items-center gap-3 rounded-lg border border-border bg-card p-3 text-left transition-all hover:border-primary/40 hover:bg-primary/5 active:scale-[0.99]"
              onClick={() => handleCategoryClick(key)}
            >
              <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                <Icon className="size-5" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="font-semibold text-foreground">{label}</p>
                {typesByCategory[key] && (
                  <p className="truncate text-xs text-muted-foreground">
                    {typesByCategory[key].map((t) => t.name).join(", ")}
                  </p>
                )}
              </div>
              <ChevronRight className="size-5 shrink-0 text-muted-foreground transition-transform group-hover:translate-x-0.5 group-hover:text-primary" />
            </button>
          ))}
        </div>
      </Section>

      <div className="mt-6 space-y-2">
        <Button asChild variant="outline" size="lg" className="w-full">
          <Link to="/documents/new" search={{ category: "other" }}>
            Upload other document
          </Link>
        </Button>
        {documents.length > 0 && (
          <Button
            variant="ghost"
            size="lg"
            className="w-full"
            onClick={() => navigate({ to: "/steps" })}
          >
            Done
          </Button>
        )}
      </div>
    </PageShell>
  );
}

function DocumentCard({
  document,
  documentTypes,
  onDelete,
  deleting,
}: {
  document: Document;
  documentTypes: DocumentType[];
  onDelete: (id: string) => void;
  // One delete at a time: the mutation's error and pending state track only the latest.
  deleting: boolean;
}) {
  const updateDocument = useUpdateDocumentMutation();
  const { data: formOptions } = useQuery(formOptionsQueryOptions);
  const isPdf = document.content_type === "application/pdf";
  const category = documentCategory(document, documentTypes);
  const categoryLabel = labelFor(
    formOptions?.document_categories,
    category,
    category.replace(/_/g, " "),
  );

  return (
    <div className="flex gap-4 rounded-xl border border-border bg-card p-4 shadow-[var(--shadow-card)]">
      <div className="flex size-14 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-border bg-muted/40">
        {document.attachment_url && !isPdf ? (
          <img
            src={document.attachment_url}
            alt={document.document_type_name}
            className="size-full object-cover"
          />
        ) : (
          <FileText className="size-6 text-muted-foreground" />
        )}
      </div>
      <div className="min-w-0 flex-1">
        <div className="mb-1 flex items-center gap-2">
          <span className="inline-flex items-center rounded-full bg-muted px-2 py-0.5 text-[0.68rem] font-semibold tracking-wide text-muted-foreground uppercase">
            {categoryLabel}
          </span>
        </div>
        <p className="truncate font-semibold text-foreground">
          {document.document_type_name}
        </p>
        <p className="text-xs text-muted-foreground tabular-nums">
          Uploaded {format(parseISO(document.created_at), "P")}
        </p>
        {documentTypes.length > 0 && (
          <div className="mt-2">
            <Select
              value={document.uploaded_document_type_id}
              onValueChange={(val) =>
                updateDocument.mutate({
                  id: document.id,
                  data: { uploaded_document_type_id: val },
                })
              }
            >
              <SelectTrigger className="h-8 w-auto min-w-48 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <GroupedDocumentTypeOptions types={documentTypes} />
              </SelectContent>
            </Select>
          </div>
        )}
      </div>
      <Button
        variant="ghost"
        size="icon-sm"
        onClick={() => onDelete(document.id)}
        disabled={deleting}
        aria-label="Remove document"
        className="shrink-0 self-start text-muted-foreground hover:text-destructive"
      >
        <Trash2 className="size-4" />
      </Button>
    </div>
  );
}
